import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Badge } from "components/ui/badge";
import { AlertCircle, Loader2, FileText } from "lucide-react";
import { Alert, AlertDescription } from "components/ui/alert";
import { toast } from "sonner";
import {
  createCallScriptFromTemplate,
  type CreateFromTemplateRequest,
} from "utils/callScriptsApi";

interface CreateFromTemplateDialogProps {
  open: boolean;
  teamId: string;
  onOpenChange: (open: boolean) => void;
  onSuccess: (callScriptId: string) => void;
}

// Procedure code format validation: 5 digits (medical) or D followed by 4 digits (dental)
const PROCEDURE_CODE_REGEX = /^(D\d{4}|\d{5})$/;

const CreateFromTemplateDialog: React.FC<CreateFromTemplateDialogProps> = ({
  open,
  teamId,
  onOpenChange,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [procedureCodesInput, setProcedureCodesInput] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [codesError, setCodesError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setName("");
      setProcedureCodesInput("");
      setNameError(null);
      setCodesError(null);
      setCreating(false);
    }
  }, [open]);

  // Parse procedure codes from comma-separated input
  const parseProcedureCodes = (input: string): string[] => {
    return input
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter((code) => code.length > 0);
  };

  const parsedCodes = parseProcedureCodes(procedureCodesInput);

  const validate = (): boolean => {
    let valid = true;

    // Validate name
    if (!name.trim()) {
      setNameError("Call script name is required");
      valid = false;
    } else {
      setNameError(null);
    }

    // Validate procedure codes
    if (parsedCodes.length === 0) {
      setCodesError("At least one procedure code is required");
      valid = false;
    } else {
      const invalidCodes = parsedCodes.filter(
        (code) => !PROCEDURE_CODE_REGEX.test(code)
      );
      if (invalidCodes.length > 0) {
        setCodesError(
          `Invalid procedure code(s): ${invalidCodes.join(", ")}. Procedure codes must be either 5 digits (medical, e.g., 94625) or D followed by 4 digits (dental, e.g., D0120).`
        );
        valid = false;
      } else {
        setCodesError(null);
      }
    }

    return valid;
  };

  const handleCreate = async () => {
    if (!validate()) return;

    setCreating(true);
    try {
      const request: CreateFromTemplateRequest = {
        team_id: teamId,
        name: name.trim(),
        procedure_codes: parsedCodes,
      };

      const response = await createCallScriptFromTemplate(request);

      toast.success("Call script created from template!", {
        description: `Created "${name.trim()}" with ${parsedCodes.length} procedure code(s).`,
      });

      onSuccess(response.call_script_id);
    } catch (err: any) {
      console.error("Error creating call script from template:", err);
      const errorMessage =
        err.response?.data?.detail ||
        "Failed to create call script from template. Please try again.";
      toast.error("Failed to create call script", {
        description: errorMessage,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (nameError) setNameError(null);
  };

  const handleCodesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProcedureCodesInput(e.target.value);
    if (codesError) setCodesError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && name.trim() && parsedCodes.length > 0) {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#005151]" />
            Create from Default Template
          </DialogTitle>
          <DialogDescription>
            Create a new call script pre-filled with standard eligibility questions.
            Benefit details questions will be generated for each procedure code you specify.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Call Script Name */}
          <div className="space-y-2">
            <Label htmlFor="template-name" className="text-sm font-medium text-gray-700">
              Call Script Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="template-name"
              type="text"
              placeholder="e.g., Acme Chiropractic - Standard"
              value={name}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              className={nameError ? "border-red-500 focus:ring-red-500" : ""}
              autoFocus
              disabled={creating}
            />
            {nameError && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{nameError}</AlertDescription>
              </Alert>
            )}
          </div>

          {/* Procedure Codes */}
          <div className="space-y-2">
            <Label htmlFor="template-codes" className="text-sm font-medium text-gray-700">
              Procedure Codes <span className="text-red-500">*</span>
            </Label>
            <Input
              id="template-codes"
              type="text"
              placeholder="e.g., D2740, D1110, D0120"
              value={procedureCodesInput}
              onChange={handleCodesChange}
              onKeyDown={handleKeyDown}
              className={codesError ? "border-red-500 focus:ring-red-500" : ""}
              disabled={creating}
            />
            {codesError && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{codesError}</AlertDescription>
              </Alert>
            )}
            <p className="text-xs text-gray-500">
              Enter procedure codes separated by commas. For each code, 9 benefit details
              questions will be created.
            </p>

            {/* Parsed codes preview */}
            {parsedCodes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {parsedCodes.map((code, idx) => (
                  <Badge
                    key={`${code}-${idx}`}
                    variant="outline"
                    className="text-xs font-mono"
                  >
                    {code}
                  </Badge>
                ))}
                <span className="text-xs text-gray-500 self-center ml-1">
                  {parsedCodes.length} code{parsedCodes.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            className="bg-[#005151] hover:bg-[#004040] text-white"
            disabled={creating || !name.trim() || parsedCodes.length === 0}
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Call Script"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFromTemplateDialog;
